/**
 * @typedef {Object} SWYMProduct
 * @property {number|string} epi - Variant ID
 * @property {number|string} empi - Product ID
 * @property {string} du - Canonical product URL
 * @property {number} qty - Quantity
 * @property {Object} cprops - Custom properties (item-level)
 */

/**
 * SWYM load callback
 * Executed after SWYM has loaded all necessary resources.
 *
 * @param {object} swat
 * @param {any} swat.evtLayer - Swym event layer instance
 * @param {Function} swat.fetchLists - Fetches all user lists
 * @param {Function} swat.updateListItem - Updates a wishlist item
 */
function onSwymLoadCallback(swat) {
  if (swat) {
    console.log("Swym scripts tags are installed on your store");

    /** @type {string} */
    let event = "sw:addedtowishlist";

    /**
     * fetchLists success callback
     *
     * @param {Array<Object>} lists
     */
    let onSuccess = function (lists) {
      console.log("Fetched all Lists", lists);
      window.dispatchEvent(
        new CustomEvent("swym:lists:fetched", { detail: { lists } }),
      );
    };

    /**
     * fetchLists error callback
     *
     * @param {Object} error
     */
    let onError = function (error) {
      console.log("Error while fetching all Lists", error);
    };

    let refetchLists = function () {
      swat.fetchLists({
        callbackFn: onSuccess,
        errorFn: onError,
      });
    };

    /**
     * Event listener triggered when a product is added to wishlist
     *
     * @param {object} e
     * @param {object} e.detail
     * @param {object} e.detail.d - SWYM payload for added product
     */
    let listener = function (e) {
      var data = /** @type {SWYMProduct} */ (e.detail.d);
      console.log("YAY! Added the product to wishlist", data);
      const favrotiesLinkBtn = document.querySelectorAll(".js-favorites-link ");
      favrotiesLinkBtn.forEach((el) => {
        setTimeout(() => {
          el.classList.add("swym-highlight");
        }, 200);
        setTimeout(() => {
          el.classList.remove("swym-highlight");
        }, 2200);
      });
      refetchLists();
    };

    /**
     * Event listener triggered when a product is removed from wishlist
     *
     * @param {object} e
     * @param {object} e.detail
     * @param {object} e.detail.d - SWYM payload for removed product
     */
    let removeListener = function (e) {
      var data = /** @type {SWYMProduct} */ (e.detail.d);
      console.log("Removed the product from wishlist", data);
      refetchLists();
    };

    // Register event listener
    swat.evtLayer.addEventListener(event, listener);

    // Register remove listener
    swat.evtLayer.addEventListener("sw:removedfromwishlist", removeListener);

    // Initial list fetch
    refetchLists();
    /** @ts-ignore */
    window.swat = swat;
  }
}

/** @ts-ignore */
if (!window.SwymCallbacks) {
  /** @ts-ignore */
  window.SwymCallbacks = [];
}
/** @ts-ignore */
window.SwymCallbacks.push(onSwymLoadCallback);

window.addEventListener("swym:lists:fetched", function (e) {
  console.log("Lists fetched event received", e.detail.lists);
  const favrotiesBtn = document.querySelectorAll(".js-favorite-btn-counter");
  if (favrotiesBtn.length > 0) {
    const lists = e.detail.lists;
    let totalItems = lists[0]?.listcontents?.length || 0;
    window.list = lists[0].lid;
    favrotiesBtn.forEach((el) => {
      el.innerText = totalItems > 0 ? `${totalItems}` : "";
    });
  }

  // Check which products are already in favorites and mark them
  markFavoriteElements(e.detail.lists);

  // Remove stale wishlist items once per session
  cleanupUnavailableWishlistItems(e.detail.lists);
});

/**
 * Mark elements that are already in the wishlist/favorites.
 * Goes through all wishlist button elements and adds "el-in-favorites" class
 * if the product is found in any of the user's lists.
 *
 * @param {Array<Object>} lists - Array of user's wishlist lists from Swym
 */
const markFavoriteElements = (lists) => {
  // Selector for all wishlist-related elements
  console.log("Marking favorite elements", lists);
  const selector =
    ".js-add-to-wishlist, .js-move-to-favorite, .js-add-product-to-favorite";
  const elements = document.querySelectorAll(selector);
  console.log("Wishlist button elements found", elements);

  if (!lists || lists.length === 0 || elements.length === 0) {
    return;
  }

  // Collect all wishlist items from all lists into a Set for fast lookup
  // Using empi (product ID) as the primary key since epi (variant ID) might differ
  const wishlistProductIds = new Set();
  const wishlistVariantIds = new Set();

  lists.forEach((list) => {
    if (list.listcontents && Array.isArray(list.listcontents)) {
      list.listcontents.forEach((item) => {
        if (item.empi) wishlistProductIds.add(String(item.empi));
        if (item.epi) wishlistVariantIds.add(String(item.epi));
      });
    }
  });

  // Check each element and mark if in favorites
  elements.forEach((el) => {
    const empi = el.dataset.empi;
    const epi = el.dataset.epi;

    // Check if product or variant is in wishlist
    const isInFavorites =
      (empi && wishlistProductIds.has(String(empi))) ||
      (epi && wishlistVariantIds.has(String(epi)));

    if (isInFavorites) {
      el.classList.add("el-in-favorites");
    } else {
      el.classList.remove("el-in-favorites");
    }
  });
};

/**
 * Removes wishlist items whose products no longer exist in the store (deleted/archived).
 * Runs once per browser session via sessionStorage to avoid repeated requests on every load.
 *
 * @param {Array<Object>} lists
 */
/**
 * @typedef {Object} SWYMList
 * @property {string} lid - List ID
 * @property {Array<SWYMProduct & { du: string }>} listcontents - Items in the list
 */

const CLEANUP_CACHE_KEY = "swym:cleanup-ts";
const CLEANUP_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Module-level guard prevents concurrent runs within the same page load
let _cleanupRunning = false;

const cleanupUnavailableWishlistItems = async (
  /** @type {SWYMList[]} */ lists,
) => {
  if (_cleanupRunning) return;

  const lastRun = parseInt(localStorage.getItem(CLEANUP_CACHE_KEY) || "0", 10);
  const due = Date.now() - lastRun > CLEANUP_INTERVAL_MS;
  if (!due) return;

  /** @ts-ignore */
  if (!window.swat || !lists?.length) return;

  _cleanupRunning = true;

  /** @ts-ignore */
  const swat = window.swat;
  const list = lists[0];
  const items = list?.listcontents;

  if (!items?.length) {
    localStorage.setItem(CLEANUP_CACHE_KEY, String(Date.now()));
    _cleanupRunning = false;
    return;
  }

  console.group(`[Swym Cleanup] Checking ${items.length} wishlist item(s)...`);

  // HEAD requests check status without downloading the response body
  const results = await Promise.allSettled(
    items.map(async (/** @type {SWYMProduct & { du: string }} */ item) => {
      try {
        const pathname = new URL(item.du).pathname;
        const res = await fetch(`${pathname}.js`, { method: "HEAD" });
        const remove = !res.ok;
        console.log(
          `%c[Swym Cleanup] ${remove ? "✗ NOT FOUND" : "✓ OK"} (${res.status}) — ${item.du}`,
          `color: ${remove ? "tomato" : "mediumseagreen"}`,
        );
        return { item, remove };
      } catch (err) {
        console.warn(`[Swym Cleanup] Network error checking ${item.du}`, err);
        return { item, remove: false };
      }
    }),
  );

  const toRemove = results
    .filter(
      (/** @type {any} */ r) => r.status === "fulfilled" && r.value.remove,
    )
    .map((/** @type {any} */ r) => r.value.item);

  if (toRemove.length === 0) {
    console.log("[Swym Cleanup] All items are valid, nothing to remove.");
    console.groupEnd();
    localStorage.setItem(CLEANUP_CACHE_KEY, String(Date.now()));
    _cleanupRunning = false;
    return;
  }

  console.log(
    `[Swym Cleanup] Removing ${toRemove.length} unavailable item(s):`,
    toRemove.map((/** @type {SWYMProduct & { du: string }} */ i) => i.du),
  );

  const removeResults = await Promise.allSettled(
    toRemove.map(
      (/** @type {SWYMProduct & { du: string }} */ item) =>
        new Promise((resolve, reject) => {
          // Only pass fields the Swym API accepts — raw listcontents items
          // contain internal fields (di, cts, lid, pid, id, uts, _pkey, _t)
          // that the delete endpoint explicitly disallows
          const payload = { epi: item.epi, empi: item.empi, du: item.du };
          swat.deleteFromList(list.lid, payload, resolve, reject);
        }),
    ),
  );

  const failed = [];
  removeResults.forEach((/** @type {any} */ r, i) => {
    if (r.status === "fulfilled") {
      console.log(
        `%c[Swym Cleanup] Removed: ${toRemove[i].du}`,
        "color: mediumseagreen",
      );
    } else {
      failed.push(toRemove[i]);
      console.error(
        `[Swym Cleanup] Failed to remove: ${toRemove[i].du}`,
        r.reason,
      );
    }
  });

  console.groupEnd();

  // Only cache the timestamp if all removals succeeded — failed items will retry next time
  if (failed.length === 0) {
    localStorage.setItem(CLEANUP_CACHE_KEY, String(Date.now()));
  }
  _cleanupRunning = false;

  // Refetch to update counter and UI after cleanup
  swat.fetchLists({
    callbackFn: (/** @type {SWYMList[]} */ updatedLists) => {
      window.dispatchEvent(
        new CustomEvent("swym:lists:fetched", {
          detail: { lists: updatedLists },
        }),
      );
    },
    errorFn: (/** @type {any} */ err) =>
      console.error("Swym refetch error after cleanup", err),
  });
};

/**
 * @param {string} lid
 */

const generateShareableLink = (lid) => {
  /** @ts-ignore */
  if (!window.swat) {
    console.log("Swym is not initialized yet");
    return;
  }
  /** @ts-ignore */
  const swat = window.swat;
  /**
   *
   * @param {string} link
   * @param {string} lid
   */
  let onSuccess = (link, lid) => {
    console.log("Successfully Generated Link ", link, lid);
    navigator.clipboard
      .writeText(link)
      .then(() => {
        const event = new CustomEvent("swym:link:shared", {
          detail: { link, lid },
        });
        window.dispatchEvent(event);
      })
      .catch((err) => {
        console.error("Could not copy text: ", err);
      });
  };
  /**
   *
   * @param {string} error
   */
  let onError = (error) => {
    // Error is an xhrObject
    console.log("Error while generating the link", error);
  };

  swat.generateSharedListURL(lid, onSuccess, onError);
};

/**
 * Initialize click handlers for "Add to Wishlist" buttons.
 * Attaches event listeners to elements matching the provided selector.
 *
 * Required data attributes on each element:
 * - data-epi: Variant ID (required)
 * - data-empi: Product ID (required)
 * - data-du: Canonical product URL (required)
 * - data-qty: Quantity (optional, defaults to 1)
 * - data-source: Source of the add action (optional, e.g., "pdp", "collections-grid", "quick-view")
 *
 * @param {string} selector - CSS selector for wishlist button elements (e.g., ".js-add-to-wishlist")
 */
const initAddToWishlistButtons = (selector) => {
  /** @ts-ignore */
  if (!window.swat) {
    console.log("Swym is not initialized yet");
    return;
  }

  /** @ts-ignore */
  const swat = window.swat;
  const buttons = document.querySelectorAll(selector);

  buttons.forEach((button) => {
    // Skip if already bound to avoid duplicate listeners after re-init
    if (button.hasAttribute("data-swym-bound")) return;
    button.setAttribute("data-swym-bound", "");
    button.addEventListener("click", function (e) {
      e.preventDefault();

      const el = /** @type {HTMLElement} */ (this);

      // Get required data attributes
      const epi = el.dataset.epi;
      const empi = el.dataset.empi;
      let du = el.dataset.du;

      // Validate required fields
      if (!epi || !empi || !du) {
        console.error(
          "Missing required data attributes (epi, empi, du) on wishlist button",
        );
        return;
      }

      // Ensure du is a full canonical URL (Swym requires full URL, not relative path)
      if (du && !du.startsWith("http")) {
        du = window.location.origin + (du.startsWith("/") ? du : "/" + du);
      }

      // Get optional data attributes
      const qty = parseInt(el.dataset.qty || "1", 10);
      const source = el.dataset.source;

      // Build product object
      /** @type {SWYMProduct & { source?: string }} */
      const product = {
        epi: epi,
        empi: empi,
        du: du,
        qty: qty,
        cprops: {},
      };

      // Add source if provided
      if (source) {
        product.source = source;
      }

      /**
       * Success callback
       * @param {Object} addedListItem
       */
      const onSuccess = async (addedListItem) => {
        console.log(`Successfully added product to wishlist`, addedListItem);
        el.classList.add("swym-added");
        window.dispatchEvent(
          new CustomEvent("swym:product:added", {
            detail: { product: addedListItem },
          }),
        );

        // Check if this button is inside a cart-item (move to favorite from cart)
        const cartItem = el.closest("cart-item");
        if (cartItem) {
          const lineKey = cartItem.dataset.line;
          if (lineKey) {
            try {
              // Remove item from cart
              const response = await fetch("/cart/change.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  id: lineKey,
                  quantity: 0,
                }),
              });

              if (response.ok) {
                console.log("Item removed from cart after adding to favorites");

                // Refresh cart UI FIRST so new DOM is in place
                const cartDrawer = document.querySelector("cart-drawer");
                if (
                  cartDrawer &&
                  typeof cartDrawer.refreshCart === "function"
                ) {
                  await cartDrawer.refreshCart();
                }

                // THEN refetch Swym lists (re-inits buttons on new DOM)
                swat.fetchLists({
                  callbackFn: (lists) => {
                    console.log(
                      "Lists refetched after moving to favorites",
                      lists,
                    );
                    window.dispatchEvent(
                      new CustomEvent("swym:lists:fetched", {
                        detail: { lists },
                      }),
                    );
                  },
                  errorFn: (error) => {
                    console.log("Error while refetching lists", error);
                  },
                });
              } else {
                console.error("Failed to remove item from cart");
              }
            } catch (error) {
              console.error("Error removing item from cart:", error);
            }
          }
        }
      };

      /**
       * Error callback
       * @param {Object} error
       */
      const onError = (error) => {
        console.error("Error while adding product to wishlist", error);
        window.dispatchEvent(
          new CustomEvent("swym:product:add:error", {
            detail: { error, product },
          }),
        );
      };

      // Get the list ID (use the default list from window.list or fetch it)
      /** @ts-ignore */
      let lid = window.list;

      if (!lid) {
        console.log("No list ID available, creating a new wishlist...");

        /**
         * Success callback for list creation
         * @param {Object} newList
         */
        const createListSuccess = (newList) => {
          console.log("Successfully created new wishlist", newList);
          /** @ts-ignore */
          window.list = newList.lid;
          lid = newList.lid;

          // Refetch lists to update UI and trigger swym:lists:fetched event
          swat.fetchLists({
            callbackFn: (lists) => {
              console.log("Lists refetched after creation", lists);
              window.dispatchEvent(
                new CustomEvent("swym:lists:fetched", { detail: { lists } }),
              );

              // Now add the product to the newly created list
              swat.addToList(lid, product, onSuccess, onError);
            },
            errorFn: (error) => {
              console.log("Error while refetching lists", error);
              // Still try to add the product even if refetch fails
              swat.addToList(lid, product, onSuccess, onError);
            },
          });
        };

        /**
         * Error callback for list creation
         * @param {Object} error
         */
        const createListError = (error) => {
          console.error("Error while creating new wishlist", error);
          window.dispatchEvent(
            new CustomEvent("swym:product:add:error", {
              detail: { error, product },
            }),
          );
        };
        const listConfig = {
          lname: "My Wishlist", // unique list name.
        };
        // Create a new wishlist
        swat.createList(listConfig, createListSuccess, createListError);

        return; // Exit here as the product will be added in the createListSuccess callback
      }

      // Call SWYM API
      swat.addToList(lid, product, onSuccess, onError);
    });
  });
};

/**
 * Auto-initialize wishlist buttons when Swym is ready.
 * Call this with your selector to set up the buttons.
 *
 * Example usage:
 * initAddToWishlistOnReady(".js-add-to-wishlist");
 *
 * @param {string} selector - CSS selector for wishlist button elements
 */
const initAddToWishlistOnReady = (selector) => {
  // If swat is already available, initialize immediately
  /** @ts-ignore */
  if (window.swat && window.list) {
    initAddToWishlistButtons(selector);
    return;
  }

  // Otherwise, wait for lists to be fetched
  window.addEventListener("swym:lists:fetched", function () {
    initAddToWishlistButtons(selector);
  });
};

initAddToWishlistOnReady(
  ".js-add-to-wishlist, .js-move-to-favorite, .js-add-product-to-favorite",
);

// Re-attach wishlist listeners after the cart drawer replaces its DOM
window.addEventListener("cart:refreshed", function () {
  if (window.swat && window.list) {
    initAddToWishlistButtons(
      ".js-add-to-wishlist, .js-move-to-favorite, .js-add-product-to-favorite",
    );
  }
});
